import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Image } from "lucide-react";

export function GraphModal({
  selectedGraph,
  selectedRecord,
  setSelectedGraph,
  report,
  apiBase,
  buildAttachmentUrl,
  getDirectionIcon,
  getDisplayName,
  getRiskBadgeClass,
  graphAttachmentChoices,
}) {
  return (
    <Dialog open={!!selectedGraph} onOpenChange={() => setSelectedGraph(null)}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-auto" closeTestId="graph-modal-close">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getDirectionIcon(selectedRecord?.direction)}
            <span className="font-mono">{getDisplayName(selectedRecord?.symbol) || "Analysis"}</span>
            <span className="text-slate-500 font-normal">—</span>
            <span className="text-slate-600 font-normal">{selectedRecord?.tc_date || "Graph"}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Review the selected LPPL chart, record details, and switch between other available graph attachments for this ticker.
          </DialogDescription>
        </DialogHeader>
        {selectedGraph && (
          <div className="flex flex-col gap-4">
            {selectedRecord && (
              <div className="grid grid-cols-4 gap-3 p-3 bg-slate-50 rounded-lg text-sm">
                <div>
                  <span className="text-xs text-slate-500 uppercase">Confidence</span>
                  <p className="font-mono font-medium">{selectedRecord.confidence || "N/A"}</p>
                </div>
                <div>
                  <span className="text-xs text-slate-500 uppercase">Risk Score</span>
                  <p className={getRiskBadgeClass(selectedRecord.risk_score)}>{selectedRecord.risk_score || "N/A"}</p>
                </div>
                <div>
                  <span className="text-xs text-slate-500 uppercase">Direction</span>
                  <p className="font-medium">{selectedRecord.direction || "N/A"}</p>
                </div>
                <div>
                  <span className="text-xs text-slate-500 uppercase">Horizon</span>
                  <p className="font-mono">{selectedRecord.horizon || "N/A"}</p>
                </div>
              </div>
            )}

            {selectedGraph.noGraph ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 bg-slate-50 rounded-lg border border-dashed border-slate-300 w-full">
                <Image className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-slate-600 font-medium">{selectedGraph.errorMessage || 'The chart is missing.'}</p>
                <p className="text-xs text-slate-400 mt-1">Select one of the available charts for this ticker below.</p>

                {selectedGraph.allAttachments && selectedGraph.allAttachments.length > 0 && (
                  <div className="mt-4 w-full max-w-2xl">
                    <div className="flex flex-wrap gap-2 justify-center">
                      {selectedGraph.allAttachments.slice(0, 12).map((attachment, idx) => (
                        <Button
                          key={attachment.filename}
                          data-testid={`missing-graph-attachment-button-${idx}`}
                          variant="outline"
                          size="sm"
                          className="text-xs"
                          onClick={() => setSelectedGraph({
                            ...selectedGraph,
                            noGraph: false,
                            errorMessage: null,
                            url: buildAttachmentUrl(apiBase, report.id, attachment.filename),
                            filename: attachment.filename,
                          })}
                        >
                          {attachment.filename}
                        </Button>
                      ))}
                    </div>
                    {selectedGraph.allAttachments.length > 12 && (
                      <p className="text-xs text-slate-400 text-center mt-2">
                        Showing first 12 charts. Refine by clicking a different TC row if needed.
                      </p>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="flex justify-center">
                  <img
                    src={selectedGraph.url}
                    alt={selectedGraph.filename}
                    className="graph-modal-image max-w-full rounded-lg border border-slate-200"
                    onError={() => {
                      setSelectedGraph((prev) => prev ? {
                        ...prev,
                        noGraph: true,
                        url: null,
                        errorMessage: `Unable to load ${prev.filename}.`,
                      } : prev);
                    }}
                  />
                </div>
                <p className="text-xs text-slate-500 text-center">{selectedGraph.filename}</p>
              </>
            )}

            {graphAttachmentChoices.length > 0 && (
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs text-slate-500 mb-2">Other available graphs:</p>
                <div className="flex flex-wrap gap-2">
                  {graphAttachmentChoices.map((attachment, idx) => (
                    <Button
                      key={attachment.filename}
                      data-testid={`graph-attachment-button-${idx}`}
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => setSelectedGraph({
                        ...selectedGraph,
                        errorMessage: null,
                        url: buildAttachmentUrl(apiBase, report.id, attachment.filename),
                        filename: attachment.filename,
                      })}
                    >
                      {attachment.filename.split('_').slice(0, 2).join('_')}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}